// Need to use the React-specific entry point to import createApi
import { createApi } from '@reduxjs/toolkit/query/react';
import config from '../../config/config';
import axiosBaseQuery from './axiosBaseQuery/axiosBaseQuery';
import {
  ApiResponse,
  CreateUserRequest,
  OptionRecord,
  OptionsBody,
  UpdateUserRequest,
  UserDetailRecord,
  UserRecord,
} from './types/types';
import { handleResponse } from '../../utils/api';

// Define a service using a base URL and expected endpoints
export const usersAPI = createApi({
  reducerPath: 'usersAPI',
  baseQuery: axiosBaseQuery({
    baseUrl: config?.appUrl,
  }),
  keepUnusedDataFor: 900,
  endpoints: (builder) => ({
    // Queries
    getUsers: builder.query<UserRecord[], void>({
      query: () => ({
        url: `/auth/users`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<UserRecord[]>) => response.body,
    }),
    getUserDetailsByNativeId: builder.query<UserDetailRecord, string>({
      query: (nativeId: string) => {
        return {
          url: `/auth/user/native/${nativeId}/detail`,
          method: 'GET',
        };
      },
      transformResponse: (response: ApiResponse<UserDetailRecord>) => handleResponse(response).body,
    }),
    getUserCountryCodes: builder.query<OptionRecord[], void>({
      query: () => ({
        url: `/admin/optionmasters/application/admin/name/user-countrycode`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<OptionsBody>) => response.body.options,
    }),
    getUserAffiliateCodes: builder.query<OptionRecord[], void>({
      query: () => ({
        url: `/admin/optionmasters/application/admin/name/user-affiliatecode`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<OptionsBody>) => response.body.options,
    }),

    // Mutations
    createUser: builder.mutation({
      query: (request: CreateUserRequest) => ({
        url: '/auth/user/create',
        method: 'POST',
        data: request,
      }),
      transformResponse: (response: ApiResponse<UserDetailRecord>) => handleResponse(response).body,
    }),
    updateUser: builder.mutation({
      query: (request: UpdateUserRequest) => ({
        url: '/auth/user/update',
        method: 'POST',
        data: request,
      }),
      transformResponse: (response: ApiResponse<UserDetailRecord>) => handleResponse(response).body,
    }),
    deleteUser: builder.mutation({
      query: (id: number) => ({
        url: `/auth/user/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetUsersQuery,
  useGetUserDetailsByNativeIdQuery,
  useLazyGetUserDetailsByNativeIdQuery,
  useGetUserCountryCodesQuery,
  useGetUserAffiliateCodesQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = usersAPI;
